import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiPaperclip, FiAtSign, FiSmile } from 'react-icons/fi';

import firebase from '@/config/firebase.config';
import { InitialsBadge } from '@/components/InitialsBadge';
import { Button, buttonVariants } from '@/components/Button';
import { TextArea } from '@/components/TextArea';
import { Spinner } from '@/components/Spinner/Spinner.component';
import { EmptyState } from '@/components/EmptyState';
import { addNotification, notificationStates } from '@/components/Notification';
import { CommentItem } from './CommentItem.component';
import styles from './CommentThread.module.scss';

const database = firebase.firestore();

export const CommentThread = ({ taskId, currentUser, memberDetails }) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [posting, setPosting] = useState(false);

  const commentsRef = useCallback(
    () =>
      database.collection('taskDatabase').doc(taskId).collection('comments'),
    [taskId]
  );

  const fetchComments = useCallback(async () => {
    const snapshot = await commentsRef().where('deleted', '==', false).get();
    setComments(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
  }, [commentsRef]);

  useEffect(() => {
    if (!taskId) return;
    setLoading(true);
    fetchComments().finally(() => setLoading(false));
  }, [taskId, fetchComments]);

  const { topLevel, repliesByParent } = useMemo(() => {
    const top = comments
      .filter((c) => !c.parentCommentId)
      .sort((a, b) => Number(b.id) - Number(a.id));
    const replies = {};
    comments.forEach((c) => {
      if (!c.parentCommentId) return;
      if (!replies[c.parentCommentId]) replies[c.parentCommentId] = [];
      replies[c.parentCommentId].push(c);
    });
    Object.values(replies).forEach((list) =>
      list.sort((a, b) => Number(a.id) - Number(b.id))
    );
    return { topLevel: top, repliesByParent: replies };
  }, [comments]);

  const handleAddComment = async (text, parentCommentId = null) => {
    const id = Date.now().toString();
    await commentsRef().doc(id).set({
      userId: currentUser?.id,
      comment: text,
      parentCommentId,
      deleted: false,
    });
    await fetchComments();
  };

  const handlePostTopLevel = async () => {
    if (!newComment.trim()) return;
    setPosting(true);
    try {
      await handleAddComment(newComment.trim());
      setNewComment('');
    } catch (error) {
      addNotification({
        message: 'Could not add comment',
        level: notificationStates.error,
      });
    } finally {
      setPosting(false);
    }
  };

  const handleSubmitReply = async (parentCommentId, text) => {
    try {
      await handleAddComment(text, parentCommentId);
    } catch (error) {
      addNotification({
        message: 'Could not add reply',
        level: notificationStates.error,
      });
    }
  };

  const handleSaveEdit = async (commentId, text) => {
    try {
      await commentsRef().doc(commentId).update({ comment: text });
      await fetchComments();
    } catch (error) {
      addNotification({
        message: 'Could not update comment',
        level: notificationStates.error,
      });
    }
  };

  const handleDelete = async (commentId) => {
    try {
      await commentsRef().doc(commentId).update({ deleted: true });
      await fetchComments();
    } catch (error) {
      addNotification({
        message: 'Could not delete comment',
        level: notificationStates.error,
      });
    }
  };

  return (
    <div className={styles.commentThread}>
      <div className={styles.composer}>
        <InitialsBadge
          label={currentUser?.name || currentUser?.email || '?'}
          twoLetter
        />
        <div className={styles.composerBox}>
          <TextArea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Add a comment..."
            rows={2}
          />
          <div className={styles.composerFooter}>
            <div className={styles.composerIcons} aria-hidden="true">
              <FiPaperclip size={15} />
              <FiAtSign size={15} />
              <FiSmile size={15} />
            </div>
            <Button
              text="Comment"
              variant={buttonVariants.primary}
              disabled={!newComment.trim() || posting}
              loading={posting}
              onClick={handlePostTopLevel}
            />
          </div>
        </div>
      </div>

      {loading ? (
        <div className={styles.centered}>
          <Spinner />
        </div>
      ) : topLevel.length === 0 ? (
        <EmptyState
          title="No comments yet"
          description="Be the first to add a comment on this task."
        />
      ) : (
        <div className={styles.commentList}>
          {topLevel.map((comment) => (
            <div key={comment.id}>
              <CommentItem
                comment={comment}
                author={memberDetails?.[comment.userId]}
                canModify={comment.userId === currentUser?.id}
                onSaveEdit={handleSaveEdit}
                onDelete={handleDelete}
                onSubmitReply={handleSubmitReply}
              />
              {(repliesByParent[comment.id] || []).map((reply) => (
                <CommentItem
                  key={reply.id}
                  comment={reply}
                  author={memberDetails?.[reply.userId]}
                  isReply
                  allowReply={false}
                  canModify={reply.userId === currentUser?.id}
                  onSaveEdit={handleSaveEdit}
                  onDelete={handleDelete}
                  onSubmitReply={handleSubmitReply}
                />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

CommentThread.displayName = 'CommentThread';
